// src/components/filters/MetricFilterWrapper.jsx
import React from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DashboardFieldsFilter from "./DashboardFields";
import MetricPresets from "./MetricPresets";
import { getControlTokens } from "../common/controls";

/**
 * MetricFilterWrapper - Unified wrapper combining DashboardFieldsFilter and MetricPresets
 *
 * Layout:
 * +------------------------------------+----------+
 * | Select Metric ▼                    | ★ Preset |
 * +------------------------------------+----------+
 *
 * @param {object} props
 * @param {array} props.selectedColumns - Currently selected metric fields
 * @param {function} props.setSelectedColumns - Setter for selected metrics
 * @param {function} props.handleColumnsChange - Handler for column changes
 * @param {array} props.allColDefs - All available column definitions
 * @param {boolean} props.isProUser - Whether user has pro subscription
 * @param {function} props.onUpgradeClick - Callback when pro feature is clicked
 * @param {boolean} props.allowMulti - Whether multi-select is enabled
 * @param {string} props.placeholderText - Placeholder text for the dropdown
 */
function MetricFilterWrapper({
  selectedColumns,
  setSelectedColumns,
  handleColumnsChange,
  allColDefs,
  isProUser = false,
  onUpgradeClick,
  allowMulti = false,
  placeholderText = 'Select Metric',
  showPresets = true,
  selectedCountLabel = 'Metrics',
  controlContext = 'filterBar',
  menuContext = controlContext,
  ...otherProps
}) {
  const theme = useTheme();
  const tokens = getControlTokens(theme, controlContext);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        border: `1px solid ${tokens.borderColor}`,
        borderRadius: `${tokens.radius}px`,
        overflow: 'hidden',
        height: `${tokens.height}px`,
        width: '100%',
        '& .MuiOutlinedInput-root': {
          height: `${tokens.height}px`,
        },
        '& .MuiSelect-select, & .MuiOutlinedInput-input': {
          fontSize: `${tokens.fontSize}px`,
          py: controlContext === 'objectToolbar' ? '3px' : '6px',
        },
      }}
    >
      <Box sx={{ flex: 1, '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}>
        <DashboardFieldsFilter
          selectedColumns={selectedColumns}
          setSelectedColumns={setSelectedColumns}
          handleColumnsChange={handleColumnsChange}
          allColDefs={allColDefs}
          isProUser={isProUser}
          onUpgradeClick={onUpgradeClick}
          allowMulti={allowMulti}
          placeholderText={placeholderText}
          selectedCountLabel={selectedCountLabel}
          controlContext={controlContext}
          menuContext={menuContext}
          {...otherProps}
        />
      </Box>
      {allowMulti && showPresets && (
        <MetricPresets
          selectedMetrics={selectedColumns}
          setSelectedMetrics={setSelectedColumns}
          allColDefs={allColDefs}
          leftBorderOnly={true}
        />
      )}
    </Box>
  );
}

export default MetricFilterWrapper;
